import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { i18n, RootState, useAppSelector } from "src/common";
import { colors } from "src/theme";
import { Text } from "..";

export interface VoucherInfoCardProps {
  active: boolean;
  text: string;
  voucherCode?: string;
  endDate?: string;
  showCode?: boolean;
}

const InfoCardStyle = styled.div({
  padding: "4px 8px",
  background: colors.background8,
  borderRadius: 8,
  width: '100%',
  cursor: 'pointer',
  transition: 'opacity 0.2s',
  '&:hover': {
    opacity: 0.8,
  },
  '&:active': {
    opacity: 0.6,
  },
});

const TextStyle = styled(Text)({
  weight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0px',
});

const CopiedMessageStyle = styled.span({
  marginLeft: '8px',
  color: colors.success,
  fontWeight: 'bold',
});

const CountdownStyle = styled(Text)({
  display: 'block',
  fontSize: '12px',
  opacity: 0.9,
});

const CodeStyle = styled.span({
  fontFamily: 'monospace',
  fontWeight: 600,
});

const formatTimeLeft = (endDate: string): string => {
  const endMs = new Date(endDate).getTime();
  const nowMs = Date.now();
  if (Number.isNaN(endMs) || endMs <= nowMs) return '';
  const totalSeconds = Math.floor((endMs - nowMs) / 1000);
  const d = Math.floor(totalSeconds / (60 * 60 * 24));
  const h = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const m = Math.floor((totalSeconds % (60 * 60)) / 60);
  const s = totalSeconds % 60;
  if (d > 0) return i18n.t('components.cards.voucherInfoCard.expiresIn', { days: d, hours: h, minutes: m, seconds: s });
  if (h > 0) return i18n.t('components.cards.voucherInfoCard.expiresInHours', { hours: h, minutes: m, seconds: s });
  if (m > 0) return i18n.t('components.cards.voucherInfoCard.expiresInMinutes', { minutes: m, seconds: s });
  return i18n.t('components.cards.voucherInfoCard.expiresInSeconds', { seconds: s });
};

export const VoucherInfoCard = ({ active, text, voucherCode, endDate, showCode = false }: VoucherInfoCardProps) => {
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(endDate ? formatTimeLeft(endDate) : '');

  const flowType = useAppSelector((state: RootState) => state.state.flow.flowType);

  useEffect(() => {
    if (!endDate) return () => {};
    const tick = () => setTimeLeft(formatTimeLeft(endDate));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endDate]);

  const handleClick = () => {
    if (voucherCode) {
      navigator.clipboard.writeText(voucherCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (flowType === 'agent') return null;
  if (!active) return null;
  if (endDate && !timeLeft) return null; // countdown expired, hide card
  return (
    <InfoCardStyle onClick={handleClick}>
      <TextStyle type="small" color={colors.text2}>
        {text}
        {showCode && voucherCode && (
          <div> {' '}
            <CodeStyle>KOD: {voucherCode}</CodeStyle>
          </div>
        )}
        {copied && <CopiedMessageStyle>{i18n.t('common.copied')}</CopiedMessageStyle>}
      </TextStyle>
      {timeLeft && <CountdownStyle type="small" color={colors.text2}>{timeLeft}</CountdownStyle>}
    </InfoCardStyle>
  )
}